import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { toast } from 'sonner'
import { AppHeader } from '#/components/layout/app-header'
import { Stepper } from '#/components/plan/stepper'
import { TechForm } from '#/components/plan/tech-form'
import { QuestionsForm } from '#/components/plan/questions-form'
import { GenerationProgress, useGenerationStream } from '#/components/plan/generation-progress'
import { PlanModelChip } from '#/components/common/model-picker'
import { PrdReview } from '#/components/plan/prd-review'
import { clearIdeaDraft } from '#/components/plan/idea-form'
import { api, ApiError, qk, usePlan, useUpdatePlan, type JobPayload } from '#/lib/api'
import type { ClarifyingQuestion } from '#/lib/types'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/plans/$planId')({
  validateSearch: z.object({ step: z.number().int().min(1).max(5).optional() }),
  component: Wizard,
})

function Wizard() {
  const { planId } = Route.useParams()
  const { step: qs } = Route.useSearch()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data, isLoading, error } = usePlan(planId)
  const update = useUpdatePlan(planId)
  const plan = data?.plan
  const step = qs ?? plan?.step ?? 2
  const go = (s: number) => navigate({ to: '/plans/$planId', params: { planId }, search: { step: s } })
  // `workspaceId` may come from the job-status payload before the plan query has loaded
  const toWorkspace = (workspaceId: string | undefined = plan?.workspaceId) => {
    if (!workspaceId) return false
    clearIdeaDraft()
    navigate({ to: '/workspace/$workspaceId/$view', params: { workspaceId, view: 'roadmap' }, search: { plan: planId } })
    return true
  }

  // questions (step 3)
  const [questions, setQuestions] = useState<ClarifyingQuestion[] | null>(null)
  const [qBusy, setQBusy] = useState(false)
  useEffect(() => {
    if (step !== 3 || !plan || questions || qBusy) return
    if (plan.questionAnswers?.questions?.length) {
      setQuestions(plan.questionAnswers.questions)
      return
    }
    setQBusy(true)
    api<{ questions: ClarifyingQuestion[] }>('/api/plan/questions', { method: 'POST', json: { planId } })
      .then((r) => setQuestions(r.questions))
      .catch((e) => toast.error(e instanceof ApiError ? e.message : m.common_error_generic()))
      .finally(() => setQBusy(false))
  }, [step, plan, questions, qBusy, planId])

  // generation: step 4 = PRD (write, then review), step 5 = features & tasks
  const hasDoc = (data?.documents?.length ?? 0) > 0
  const stage: 'prd' | 'features' = step >= 5 ? 'features' : 'prd'
  const streaming = (step === 4 || step === 5) && plan?.status !== 'completed'
  const [genNonce, setGenNonce] = useState(0)
  const { job, streamLost } = useGenerationStream(planId, Boolean(streaming), genNonce)
  const [localJob, setLocalJob] = useState<JobPayload | null>(null)
  const [retrying, setRetrying] = useState(false)
  // a fresh start/retry response wins over a stale stream payload until the stream catches up
  const effJob = localJob && (!job || localJob.status === 'running' && job.status === 'error') ? localJob : (job ?? localJob)
  const jobWorkspaceId = effJob && 'workspaceId' in effJob ? (effJob as { workspaceId?: string }).workspaceId : undefined
  // PRD stage finished → refetch the plan so the review view renders the document
  useEffect(() => {
    if (step === 4 && effJob?.status === 'done' && !hasDoc) qc.invalidateQueries({ queryKey: qk.plan(planId) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effJob?.status, step, hasDoc])
  // features stage finished → workspace
  useEffect(() => {
    if (step === 5 && effJob?.status === 'done' && effJob.phase !== 'review') {
      qc.invalidateQueries({ queryKey: qk.plan(planId) })
      toWorkspace(plan?.workspaceId ?? jobWorkspaceId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effJob?.status, effJob?.phase, step, plan?.workspaceId, jobWorkspaceId])
  // completed plan → never show the wizard's step 4/5, go straight to the roadmap
  useEffect(() => {
    if (plan?.status === 'completed' && (step === 4 || step === 5)) toWorkspace()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.status, step])
  const startGen = async (opts: { stage: 'prd' | 'features'; regenerate?: boolean }) => {
    setRetrying(true)
    try {
      const r = await api<{ job: JobPayload }>('/api/plan/generation/start', { method: 'POST', json: { planId, regenerate: opts.regenerate ?? false, stage: opts.stage } })
      setLocalJob(r.job)
      setGenNonce((n) => n + 1)
      return true
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
      if (e instanceof ApiError && e.status === 402) navigate({ to: '/pricing', search: { voucher: undefined } })
      return false
    } finally {
      setRetrying(false)
    }
  }

  if (error) return <p className="p-8 text-destructive">{(error as Error).message}</p>
  if (isLoading || !plan) return <div className="flex flex-1 items-center justify-center text-muted-foreground">{m.common_loading()}</div>

  return (
    <>
      <AppHeader showStats={false} center={<Stepper steps={[m.wizard_step_idea(), m.wizard_step_docs(), m.wizard_step_export()]} current={step >= 4 ? 2 : 1} />} />
      <main className={step === 4 && hasDoc && effJob?.status !== 'running' ? 'flex min-h-0 flex-1 flex-col overflow-hidden' : 'flex-1 overflow-y-auto px-6 pb-12 pt-8 sm:px-8 sm:pt-12'}>
        {step <= 2 ? (
          <TechForm
            initial={plan.techStack}
            busy={update.isPending}
            onBack={() => navigate({ to: '/plan', search: { flow: 'new' } })}
            onNext={async (techStack) => {
              await update.mutateAsync({ techStack, step: 3 })
              go(3)
            }}
          />
        ) : step === 3 ? (
          questions ? (
            <>
            <div className="mx-auto mb-3 flex w-full max-w-3xl items-center justify-end gap-2 text-xs text-muted-foreground">
              <span>{m.model_pick_label()}</span>
              <PlanModelChip planId={planId} value={plan.model ?? null} />
            </div>
            <QuestionsForm
              questions={questions}
              initial={(plan.questionAnswers?.answers as Record<string, string | string[]>) ?? {}}
              busy={update.isPending}
              onBack={() => go(2)}
              onSubmit={async (answers) => {
                await update.mutateAsync({ questionAnswers: { questions, answers }, step: 4 })
                await startGen({ stage: 'prd' })
                go(4)
              }}
            />
            </>
          ) : (
            <div className="flex items-center justify-center py-20 text-muted-foreground">{m.questions_loading()}</div>
          )
        ) : step === 4 && hasDoc && effJob?.status !== 'running' ? (
          <PrdReview
            planId={planId}
            documents={data!.documents}
            chatMessages={data?.chatMessages ?? []}
            busy={retrying}
            planModel={plan.model ?? null}
            job={effJob}
            onRegenerate={async () => {
              setLocalJob(null)
              if (await startGen({ stage: 'prd', regenerate: true })) qc.invalidateQueries({ queryKey: qk.plan(planId) })
            }}
            onContinue={async () => {
              if (await startGen({ stage: 'features' })) {
                await update.mutateAsync({ step: 5 })
                go(5)
              }
            }}
          />
        ) : (
          <GenerationProgress job={effJob} stage={stage} streamLost={streamLost} retrying={retrying} onRetry={() => startGen({ stage })} />
        )}
      </main>
    </>
  )
}
